
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit, AlertTriangle } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";
import { useToast } from "@/hooks/use-toast";

interface EditOrderDialogProps {
  order: EtsyOrder;
  onOrderUpdate: (updatedOrder: EtsyOrder) => void;
}

const EditOrderDialog = ({ order, onOrderUpdate }: EditOrderDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editedOrder, setEditedOrder] = useState<EtsyOrder>(order);
  const { toast } = useToast();

  const handleSave = () => {
    console.log("[EditOrderDialog] Before save, editedOrder:", editedOrder);
    onOrderUpdate(editedOrder);
    setIsOpen(false);
    toast({
      title: "Order Updated Locally",
      description: "Changes saved to cache only. Data will reset on next fetch from Etsy.",
      variant: "default",
    });
  };

  const handleInputChange = (field: keyof EtsyOrder, value: string | number) => {
    setEditedOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="mr-2 h-4 w-4" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Order - {order.receiptId}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Temporary Changes Notice */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Temporary Changes:</strong> These edits only update your local cache and will not affect Etsy data. Changes will be lost when you refresh or fetch new data from Etsy.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name</Label>
              <Input
                id="customerName"
                value={editedOrder.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">Customer Email</Label>
              <Input
                id="customerEmail"
                value={editedOrder.customerEmail || ''}
                onChange={(e) => handleInputChange('customerEmail', e.target.value)}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="productName">Product Name</Label>
            <Input
              id="productName"
              value={editedOrder.productName}
              onChange={(e) => handleInputChange('productName', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={editedOrder.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={editedOrder.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP Code</Label>
              <Input
                id="zip"
                value={editedOrder.zip}
                onChange={(e) => handleInputChange('zip', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={editedOrder.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trackingId">Tracking ID</Label>
              <Input
                id="trackingId"
                value={editedOrder.trackingId}
                onChange={(e) => handleInputChange('trackingId', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={editedOrder.status} onValueChange={(value) => handleInputChange('status', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Order Date</Label>
              <Input
                id="date"
                value={editedOrder.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="totalAmount">Total Amount</Label>
              <Input
                id="totalAmount"
                type="number"
                step="0.01"
                value={editedOrder.totalAmount || ''}
                onChange={(e) => handleInputChange('totalAmount', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subtotalAmount">Net Revenue</Label>
              <Input
                id="subtotalAmount"
                type="number"
                step="0.01"
                value={editedOrder.subtotalAmount || ''}
                onChange={(e) => handleInputChange('subtotalAmount', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currencyCode">Currency</Label>
              <Input
                id="currencyCode"
                value={editedOrder.currencyCode || 'USD'}
                onChange={(e) => handleInputChange('currencyCode', e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <Button onClick={handleSave}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrderDialog;
